import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Handshake, UserFocus, Warning, FileText, ClipboardText, Question } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { FAQ } from '@/components/FAQ';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const BPCLOAS = () => {
  const situacoes = [
    { icon: ClipboardText, title: 'Pedido inicial de BPC/LOAS' },
    { icon: Warning, title: 'Benefício negado' },
    { icon: FileText, title: 'Benefício suspenso' },
    { icon: Question, title: 'Dúvidas sobre documentação' },
    { icon: UserFocus, title: 'Casos envolvendo pessoa idosa' },
    { icon: Handshake, title: 'Casos envolvendo pessoa com deficiência' },
  ];

  const faqItems = [
    {
      question: 'O que fazer quando o BPC/LOAS é negado?',
      answer:
        'É importante analisar a decisão, a documentação e o contexto do caso para verificar as medidas cabíveis.',
    },
    {
      question: 'O escritório atua apenas em novos pedidos?',
      answer:
        'Não. A RDC Advogados também atua em negativas, suspensões, recursos e demais situações relacionadas ao benefício.',
    },
    {
      question: 'O caso pode ser analisado antes de qualquer medida?',
      answer:
        'Sim. A análise prévia é essencial para compreender a situação e orientar adequadamente sobre requisitos, documentos e possibilidades jurídicas.',
    },
    {
      question: 'A RDC Advogados atende no Rio de Janeiro?',
      answer:
        'Sim. O escritório está localizado no Centro do Rio de Janeiro e presta atendimento com análise técnica e comunicação clara em questões relacionadas ao BPC/LOAS.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="bpc-loas-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl font-bold playfair tracking-tight mb-6">
              BPC/LOAS no Rio de Janeiro
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Orientação jurídica em pedidos, negativas, suspensões e medidas cabíveis relacionadas ao benefício
              assistencial, com comunicação clara e análise responsável.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Opening Text */}
      <section className="py-24 bg-white" data-testid="bpc-loas-intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto">
            <p className="text-lg text-[#737373] leading-relaxed mb-6">
              O BPC/LOAS é um benefício que desperta muitas dúvidas, especialmente em razão dos requisitos legais,
              da documentação necessária e das negativas que podem ocorrer na esfera administrativa. Em situações
              assim, a orientação jurídica precisa ser ao mesmo tempo acessível, segura e tecnicamente bem
              estruturada.
            </p>
            <p className="text-lg text-[#737373] leading-relaxed">
              A RDC Advogados atua em demandas relacionadas ao benefício assistencial com foco em clareza,
              organização documental e definição responsável das medidas cabíveis, conforme as particularidades de
              cada situação.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Situações */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="bpc-loas-situacoes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">
              Quem costuma buscar esse atendimento
            </h2>
            <p className="text-lg text-[#737373]">
              Situações em que a orientação jurídica pode ser importante
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {situacoes.map((item, index) => (
              <motion.div
                key={item.title || item.id || index}
                {...fadeUp}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white border border-[#2A2A2A]/10 rounded-sm p-6 flex items-center space-x-4"
                data-testid={`situacao-${index}`}
              >
                <item.icon size={32} weight="duotone" className="text-[#A98C67] flex-shrink-0" />
                <span className="text-[#2A2A2A] font-medium">{item.title}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How we help */}
      <section className="py-24 bg-white" data-testid="bpc-loas-help-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
                Como o escritório pode ajudar
              </h2>
              <p className="text-xl text-[#737373] mb-6 leading-relaxed">Atuação com clareza e rigor técnico</p>
              <p className="text-lg text-[#737373] leading-relaxed">
                A análise envolve a compreensão do caso concreto, verificação documental, leitura da situação
                apresentada e orientação sobre as medidas administrativas ou judiciais cabíveis. O compromisso do
                escritório é conduzir o cliente com linguagem clara, critério técnico e seriedade institucional.
              </p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.6 }}>
              <img
                src="https://images.unsplash.com/photo-1758519288480-1489c17b1519"
                alt="Atendimento jurídico"
                className="rounded-sm shadow-lg w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sensitive Matter */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="bpc-loas-sensitive-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
              Atendimento humano sem perder precisão
            </h2>
            <p className="text-xl text-[#737373] mb-8 leading-relaxed">
              Segurança jurídica em uma matéria sensível
            </p>
            <p className="text-lg text-[#737373] leading-relaxed">
              Questões relacionadas ao BPC/LOAS costumam afetar diretamente a subsistência do requerente e de sua
              família. Por isso, o atendimento precisa ser realizado com respeito ao contexto humano do caso, sem
              abrir mão da precisão jurídica necessária para uma atuação consistente.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-testid="bpc-loas-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">Perguntas frequentes</h2>
          </motion.div>
          <motion.div {...fadeUp}>
            <FAQ items={faqItems} />
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="bpc-loas-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold playfair mb-6">
              Seu caso de BPC/LOAS merece orientação clara e análise cuidadosa
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Entre em contato com a RDC Advogados para apresentar sua situação e receber uma avaliação inicial
              com seriedade e atenção ao caso concreto.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button
                  size="lg"
                  data-testid="bpc-loas-contact-button"
                  className="bg-white text-[#2A2A2A] hover:bg-white/90 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Solicitar orientação
                </Button>
              </Link>
              <a href="https://wa.me/552128880258" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  
                  data-testid="bpc-loas-whatsapp-button"
                  className="bg-[#25D366] text-white hover:bg-[#20BA5A] border-0 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Falar com o escritório
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BPCLOAS;